import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronDown, CircleCheckBig, Folder, Inbox, Plus, StickyNote } from 'lucide-react';
import { useDialogs, useToast } from '@missions/ui';
import { api } from '../lib/client';
import { useErrorToast, useInvalidate } from '../lib/hooks';
import { useSession, useT } from '../lib/session';
import { useProjects } from '../features/missions/data';
import { useShell } from './shell-context';

/** "All projects" folder tree (secondary sidebar). */
export function ProjectTree({ activeId }: { activeId?: string }) {
  const shell = useShell();
  const s = useSession();
  const { tree, isPending } = useProjects(shell.workspaceId);
  const [closed, setClosed] = useState<Record<string, boolean>>({});
  const dialogs = useDialogs();
  const nav = useNavigate();
  const toast = useToast();
  const onErr = useErrorToast();
  const invalidate = useInvalidate();
  if (isPending) return <div className="glabel" style={{ padding: '6px 10px' }}>Loading projects…</div>;
  const folders = tree?.folders ?? [];
  const createProject = async () => {
    const name = await dialogs.prompt({
      title: 'New project',
      label: 'Project name',
      placeholder: 'e.g. Acme Corp Website Redesign',
      confirmText: 'Create project',
      validate: (v) => (v.length < 3 ? 'Use at least 3 characters' : v.length > 80 ? 'Keep it under 80 characters' : null),
    });
    if (!name) return;
    try {
      const p = await api.call('projects.create', { body: { workspaceId: shell.workspaceId, name } });
      await invalidate('projects');
      toast(`Project “${p.name}” created`);
      nav(`/p/${p.id}`);
    } catch (e) {
      onErr(e);
    }
  };
  const item = (p: { id: string; name: string; color: string; counts: { open: number } }) => (
    <li key={p.id}>
      <NavLink to={`/p/${p.id}`} className={`proj${p.id === activeId ? ' on' : ''}`} data-tooltip={p.name}>
        <span className="dot" style={{ background: p.color }} />
        <span className="lbl">{p.name}</span>
        <span className="cnt">{p.counts.open}</span>
      </NavLink>
    </li>
  );
  return (
    <>
      {folders.map((f) => (
        <div className="folder" key={f.id}>
          <button type="button" aria-expanded={!closed[f.id]} onClick={() => setClosed((c) => ({ ...c, [f.id]: !c[f.id] }))}>
            <ChevronDown className="c" style={{ transform: closed[f.id] ? 'rotate(-90deg)' : undefined }} />
            <Folder />
            {f.name}
          </button>
          {!closed[f.id] && <ul>{f.projects.map(item)}</ul>}
        </div>
      ))}
      {(tree?.rootProjects.length ?? 0) > 0 && <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{tree!.rootProjects.map(item)}</ul>}
      {s.can('project.create') && (
        <button className="newproj" onClick={() => void createProject()}>
          <Plus />New project
        </button>
      )}
    </>
  );
}

/** Rail "Go to" card. */
export function GoToCard() {
  const t = useT();
  const nav = useNavigate();
  return (
    <div className="rcard">
      <h4>Go to</h4>
      <button className="golink" onClick={() => nav('/my')}><CircleCheckBig />{t.myItems}<ArrowRight className="ar" /></button>
      <button className="golink" onClick={() => nav('/inbox')}><Inbox />Inbox<ArrowRight className="ar" /></button>
      <button className="golink" onClick={() => nav('/boards')}><StickyNote />Sticky boards<ArrowRight className="ar" /></button>
    </div>
  );
}

/** Rail "On this page" anchors. */
export function OnThisPage({ links }: { links: [string, string][] }) {
  const [on, setOn] = useState(links[0]?.[0]);
  return (
    <div className="rcard">
      <h4>On this page</h4>
      <nav className="otp">
        {links.map(([id, label]) => (
          <a
            key={id}
            href={`#${id}`}
            className={on === id ? 'on' : ''}
            onClick={(e) => {
              e.preventDefault();
              setOn(id);
              document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            {label}
          </a>
        ))}
      </nav>
    </div>
  );
}
